import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Trophy, Flame, MessageCircle, BookMarked, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { handsDashboard, handsAiChat } from "@/lib/handsApi";

export default function HandsHome() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/helpinghands"); return; }
    handsDashboard()
      .then(setDash)
      .catch((e) => { if (e.response?.status === 401) nav("/login?next=/helpinghands"); else toast.error("Couldn't load"); });
  }, [user, loading, nav]);

  const quickAsk = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await handsAiChat(text);
      nav(`/helpinghands/ask/${r.session_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reach Jixxeo A.I");
    } finally { setBusy(false); }
  };

  if (!dash) return <div className="py-16 text-center text-white/40">Loading your money…</div>;

  return (
    <div data-testid="hands-home" className="pb-6 space-y-5">
      <HeroAsk input={input} setInput={setInput} busy={busy} onSubmit={quickAsk} />

      {(dash.recent_plans || []).length > 0 && (
        <section data-testid="recent-plans">
          <div className="flex items-center mb-3">
            <h2 className="font-black text-lg">Recent plans</h2>
            <Link to="/helpinghands/plans" data-testid="see-all-plans"
              className="ml-auto text-[11px] text-white/60 hover:text-white underline">See all ({dash.plans_count})</Link>
          </div>
          <ul className="space-y-2">
            {dash.recent_plans.slice(0, 3).map((p) => (
              <li key={p.id} data-testid={`home-plan-${p.id}`}>
                <Link to={`/helpinghands/ask/${p.id}`}
                  className="flex gap-3 bg-[#0F2118] rounded-2xl border border-[#1D3A2E] p-3 hover:border-[#22C55E]/50 transition-colors">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center">
                    <Sparkles className="w-4 h-4" fill="currentColor" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{p.title || "Untitled plan"}</div>
                    <div className="text-[11px] text-white/50 line-clamp-2">{p.last_reply_preview || "Tap to resume."}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/40 self-center shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <MoreShortcuts />
    </div>
  );
}

function HeroAsk({ input, setInput, busy, onSubmit }) {
  return (
    <section data-testid="hero-ask" className="rounded-3xl bg-gradient-to-br from-[#22C55E]/25 via-[#0F2118] to-[#0A1512] border border-[#22C55E]/30 p-5 sm:p-7 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-40 bg-[#22C55E]" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 bg-black/30 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3 border border-[#22C55E]/40 text-[#22C55E]">
          <Sparkles className="w-3 h-3" fill="currentColor" /> Jixxeo A.I · money coach
        </div>
        <h1 className="font-black text-3xl sm:text-4xl leading-tight">Ask about any<br/>bill or debt.</h1>
        <p className="text-sm text-white/70 mt-2 max-w-md">Describe what's going on and Jixxeo A.I builds a personalized plan. Every reply is auto-saved so you can come back to it.</p>

        <form onSubmit={onSubmit} className="mt-5 bg-[#0A1512] rounded-2xl border border-[#1D3A2E] focus-within:border-[#22C55E]/60 p-2 flex items-end gap-2">
          <textarea data-testid="hero-ask-input" rows={2} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
            placeholder="I have $2,400 in credit card debt at 24% APR…"
            className="flex-1 bg-transparent px-3 py-2 text-sm resize-none focus:outline-none max-h-32" />
          <button data-testid="hero-ask-send" type="submit" disabled={busy || !input.trim()}
            className="shrink-0 h-10 px-4 rounded-full bg-[#22C55E] text-[#0A1512] flex items-center gap-1 font-black text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed">
            <Send className="w-3.5 h-3.5" /> Ask
          </button>
        </form>

        <Link to="/helpinghands/ask" data-testid="open-full-chat"
          className="mt-3 inline-flex items-center gap-1 text-xs text-white/60 hover:text-white underline">
          Open full chat <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </section>
  );
}

function MoreShortcuts() {
  const cards = [
    { to: "/helpinghands/plans",     icon: <BookMarked className="w-5 h-5" />,   label: "My plans",   desc: "Auto-saved advice" },
    { to: "/helpinghands/community", icon: <MessageCircle className="w-5 h-5" />, label: "Community",  desc: "Advice & tips" },
    { to: "/helpinghands/challenges", icon: <Flame className="w-5 h-5" />,        label: "Challenges", desc: "Save & earn" },
    { to: "/helpinghands/badges",    icon: <Trophy className="w-5 h-5" />,        label: "Badges",     desc: "Your milestones" },
  ];
  return (
    <section data-testid="hands-more" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {cards.map((c) => (
        <Link key={c.to} to={c.to} data-testid={`more-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
          className="bg-[#0F2118] rounded-2xl border border-[#1D3A2E] hover:border-[#22C55E]/50 transition-colors p-3 flex flex-col gap-1">
          <div className="w-8 h-8 rounded-lg bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center">{c.icon}</div>
          <div className="font-black text-sm">{c.label}</div>
          <div className="text-[10px] text-white/50">{c.desc}</div>
        </Link>
      ))}
    </section>
  );
}
